/**
 * FinishScreen — эффектный финальный экран сессии (И7, Часть 3).
 *
 * Поверх табличного ResultsBoard: подиум топ-3 (2-е слева, 1-е в центре выше, 3-е
 * справа), CSS-конфетти и поздравление. Под подиумом — компактный список остальных
 * мест и опциональный блок действий (например, кнопка «Полная таблица» у ведущего).
 *
 * Источник данных — ScoreboardEntry[] (итоговый scoreboard после события finished).
 * Универсален: используется и ведущим, и игроком (highlightNick подсвечивает себя).
 * Анимации — CSS (keyframes), без JS-библиотек; глобально уважается prefers-reduced-motion
 * (см. index.css — transition/animation отключаются).
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScoreboardEntry } from '../protocol';

export interface FinishScreenProps {
  entries: ScoreboardEntry[];
  highlightNick?: string | null;
  /** Заголовок-поздравление; по умолчанию берётся из t('finish.title'). */
  title?: string;
  /** Доп. действия под подиумом (например, кнопка «Полная таблица»). */
  actions?: ReactNode;
  /** Подзаголовок/благодарность под заголовком. */
  subtitle?: string;
}

const PODIUM_MEDALS = ['🥇', '🥈', '🥉'];

export function FinishScreen({
  entries,
  highlightNick = null,
  title,
  subtitle,
  actions,
}: FinishScreenProps): JSX.Element {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('finish.title');
  const resolvedSubtitle = subtitle ?? t('finish.thanks');
  const sorted = [...entries].sort((a, b) => b.score - a.score || a.nick.localeCompare(b.nick));
  const top3 = sorted.slice(0, 3);
  const rest = sorted.slice(3);

  // Подиум рендерится в порядке [2-е, 1-е, 3-е], чтобы 1-е оказалось в центре.
  const podiumOrder = [top3[1], top3[0], top3[2]].filter((e): e is ScoreboardEntry => Boolean(e));

  return (
    <section className="finish" aria-label={t('finish.aria')} data-testid="finish-screen">
      <div className="finish__confetti" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="finish__confetto"
            style={{ animationDelay: `${(i % 7) * 0.18}s` }}
          />
        ))}
      </div>

      <h2 className="finish__title">{resolvedTitle}</h2>
      {resolvedSubtitle ? <p className="finish__subtitle">{resolvedSubtitle}</p> : null}

      {sorted.length === 0 ? (
        <p className="muted">{t('finish.noParticipants')}</p>
      ) : (
        <>
          <div className="podium" aria-label={t('finish.podiumAria')}>
            {podiumOrder.map((entry) => {
              const rank = sorted.indexOf(entry) + 1;
              const placeClass =
                rank === 1 ? 'podium__step--1' : rank === 2 ? 'podium__step--2' : 'podium__step--3';
              const medal = rank <= PODIUM_MEDALS.length ? PODIUM_MEDALS[rank - 1] : null;
              return (
                <div
                  key={entry.nick}
                  className={`podium__step ${placeClass}${entry.nick === highlightNick ? ' podium__step--me' : ''}`}
                  data-testid={`podium-${rank}`}
                >
                  <span className="podium__medal">{medal}</span>
                  <span className="podium__nick">{entry.nick}</span>
                  <span className="podium__score">{entry.score}</span>
                  <span className="podium__place">{rank}</span>
                </div>
              );
            })}
          </div>

          {rest.length > 0 ? (
            <ol className="finish__rest" aria-label={t('finish.restAria')}>
              {rest.map((entry, i) => (
                <li
                  key={entry.nick}
                  className={`finish__rest-row${entry.nick === highlightNick ? ' finish__rest-row--me' : ''}`}
                >
                  <span className="finish__rest-rank">{i + 4}</span>
                  <span className="finish__rest-nick">{entry.nick}</span>
                  <span className="finish__rest-score">{entry.score}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </>
      )}

      {actions ? <div className="finish__actions">{actions}</div> : null}
    </section>
  );
}
